/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { homeAction } from "@redux/modules";
import { FaLock, FaChevronDown, FaInfoCircle } from "react-icons/fa";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import "./styles.scss";

const UNIT_LABELS = { ADET: "adet", KOLI: "koli", TON: "ton" };

const COLOR_PROGRESS = "#2E6DA4";
const COLOR_DONE = "#1E8449";

/** Seçicilerde gösterilen ad: bayi adı; bayi kaydı silinmişse elde yalnızca kod kalır. */
const dealerLabel = (dealer) => dealer.name || dealer.code || "";

/** Türkçe alfabeye göre sıralama (Ç, Ğ, İ, Ö, Ş, Ü doğru yere gelsin). */
const byLabel = (label) => (a, b) =>
  label(a).localeCompare(label(b), "tr", { sensitivity: "base" });

/** Bölge müdürünün kapsam seçimi: bayi hedefleri mi, satış temsilcisi hedefleri mi. */
const SCOPE_DEALER = "DEALER";
const SCOPE_SALES_REP = "SALES_REP";

const STATUS_META = {
  ACTIVE: { label: "Devam ediyor", className: "ht-badge-active" },
  COMPLETED: { label: "Tamamlandı", className: "ht-badge-completed" },
  UPCOMING: { label: "Henüz başlamadı", className: "ht-badge-upcoming" },
};

/**
 * Kategorideki ürünlerin birimleri aynıysa o birimi döner, değilse boş.
 */
const resolveUnitLabel = (products) => {
  if (!products || products.length === 0) return "";
  const units = [...new Set(products.map((p) => p.unit).filter(Boolean))];
  if (units.length === 1) {
    return UNIT_LABELS[units[0]] || "";
  }
  return "";
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (e) {
    return "";
  }
};

/** Dönem başlığındaki tarih aralığı için kısa biçim (01.08.2026). */
const formatShortDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (e) {
    return "";
  }
};

/**
 * Dönem başlığında gösterilen tarih aralığı. Aynı anda birden fazla dönem açık
 * olabildiği için (ör. çeyreklik + aylık) hangi dönemin hangi aralığı kapsadığı
 * ad ile birlikte görünür olmalıdır.
 */
const formatDateRange = (dateBegin, dateEnd) => {
  const begin = formatShortDate(dateBegin);
  const end = formatShortDate(dateEnd);
  if (!begin && !end) return "";
  return `${begin} – ${end}`;
};

/**
 * Bir hedef grubu için tüm ölçümleri (kategoriler + nokta hedefleri) üretir.
 * Her ölçüm hem bayi toplam hedefini/gerçekleşmesini (bayiTarget/bayiAchieved) hem de giriş
 * yapan BST'nin kendi payını/gerçekleşmesini (myTarget/myAchieved — myBstTarget'tan) taşır.
 * BST için dağılım yoksa myTarget null. Gerçekleşen değerler batch'ten gelir (achievedQuantity).
 */
const buildMeasures = (group) => {
  const my = group.myBstTarget || null;
  const myCategoryById = {};
  const myCategoryAchievedById = {};
  if (my) {
    (my.categories || []).forEach((c) => {
      myCategoryById[c.targetCategoryId] = c.quantity;
      myCategoryAchievedById[c.targetCategoryId] = c.achievedQuantity;
    });
  }
  const categories = group.categories || [];
  const measures = categories.map((category, ci) => ({
    key: `cat-${category.targetCategoryId ?? ci}`,
    title: category.categoryName,
    bayiTarget: category.quantity,
    bayiAchieved: category.achievedQuantity ?? 0,
    myTarget: my ? myCategoryById[category.targetCategoryId] ?? 0 : null,
    myAchieved: my ? myCategoryAchievedById[category.targetCategoryId] ?? 0 : 0,
    unitLabel: resolveUnitLabel(category.products),
  }));
  if (
    group.differentPointTarget !== null &&
    group.differentPointTarget !== undefined
  ) {
    measures.push({
      key: "different-point",
      title: "Farklı Nokta Hedefi",
      bayiTarget: group.differentPointTarget,
      bayiAchieved: group.differentPointAchieved ?? 0,
      myTarget: my ? my.differentPointTarget ?? 0 : null,
      myAchieved: my ? my.differentPointAchieved ?? 0 : 0,
      unitLabel: "nokta",
    });
  }
  if (group.newPointTarget !== null && group.newPointTarget !== undefined) {
    measures.push({
      key: "new-point",
      title: "Yeni Nokta Hedefi",
      bayiTarget: group.newPointTarget,
      bayiAchieved: group.newPointAchieved ?? 0,
      myTarget: my ? my.newPointTarget ?? 0 : null,
      myAchieved: my ? my.newPointAchieved ?? 0 : 0,
      unitLabel: "nokta",
    });
  }
  // Nokta Kayıt hedefi (POINT_REGISTRATION tipindeki hedef grubunun tek ölçümü):
  // dönem içinde "İşletmeni Getir" koduyla kaydettirilmesi beklenen işletme sayısı.
  if (
    group.targetGroupType === "POINT_REGISTRATION" ||
    (group.registrationTarget !== null &&
      group.registrationTarget !== undefined)
  ) {
    measures.push({
      key: "point-registration",
      title: "Nokta Kayıt Hedefi",
      bayiTarget: group.registrationTarget,
      bayiAchieved: group.registrationAchieved ?? 0,
      myTarget: my ? my.registrationTarget ?? 0 : null,
      myAchieved: my ? my.registrationAchieved ?? 0 : 0,
      unitLabel: "işletme",
      // Nokta Kayıt hedefi kişiseldir: bayi toplamıyla bir bağlantısı yoktur,
      // bu yüzden "Bayi Toplam Hedefi" bölümünde gösterilmez.
      personalOnly: true,
    });
  }
  return measures;
};

/**
 * Tek bir hedef kartı (donut + başlık + gerçekleşen/hedef).
 * achieved (gerçekleşen) şu an 0; ileride hesaplanınca buraya bağlanacak.
 */
const TargetDonut = ({ title, achieved = 0, target, unitLabel }) => {
  const numericTarget = Number(target) || 0;
  const percent =
    numericTarget > 0
      ? Math.min(100, Math.round((achieved / numericTarget) * 100))
      : 0;
  const isDone = percent >= 100 && numericTarget > 0;
  const color = isDone ? COLOR_DONE : COLOR_PROGRESS;

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percent / 100);

  return (
    <div className="ht-card">
      <div className="ht-card-title">{title}</div>
      <div className="ht-donut">
        <svg viewBox="0 0 120 120" className="ht-donut-svg">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#E0E6ED"
            strokeWidth="10"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 60 60)"
          />
        </svg>
        <div className="ht-donut-center">
          <span
            className="ht-percent"
            style={isDone ? { color: COLOR_DONE } : undefined}
          >
            {percent}%
          </span>
        </div>
      </div>
      <div className={`ht-card-sub ${isDone ? "done" : ""}`}>
        {achieved} / {numericTarget} {unitLabel}
      </div>
    </div>
  );
};

/**
 * Bayi toplam hedefi için kompakt yatay ilerleme çubuğu (Seçenek B — alt referans).
 */
const DealerBar = ({ title, achieved = 0, target, unitLabel }) => {
  const numericTarget = Number(target) || 0;
  const percent =
    numericTarget > 0
      ? Math.min(100, Math.round((achieved / numericTarget) * 100))
      : 0;
  const isDone = percent >= 100 && numericTarget > 0;
  return (
    <div className="ht-bar-item">
      <div className="ht-bar-label">{title}</div>
      <div className="ht-bar-track">
        <div
          className="ht-bar-fill"
          style={{
            width: `${percent}%`,
            background: isDone ? COLOR_DONE : COLOR_PROGRESS,
          }}
        />
      </div>
      <div className="ht-bar-text">
        {achieved} / {numericTarget} {unitLabel}
      </div>
    </div>
  );
};

/**
 * Home Hedef Durumu Bölümü
 *
 * Satış temsilcisi (USER) için, bağlı olduğu bayiye atanmış hedefleri kampanya
 * dönemine göre akordiyon olarak gösterir. Aynı anda birden fazla dönem açık
 * olabilir (ör. çeyreklik ve aylık dönem aynı ayı kapsar); her dönem kendi
 * hedefleri ve gerçekleşmesiyle ayrı bir kart olarak listelenir:
 *  - Geçmiş dönemler (Tamamlandı): tek bir "Geçmiş dönemler (N)" bloğunun altında
 *    toplanır; blok kapalı gelir, açılınca dönemler yine kapalı listelenir.
 *  - Aktif dönemler (Devam ediyor): hepsi açık gelir, bitişi yakın olan en üstte.
 *  - Gelecek dönemler (Henüz başlamadı): kilitli, açılamaz.
 * Backend listeyi bu sırayla (geçmiş → aktif → gelecek) döndürür; burada yalnızca
 * geçmişler ayrı bloğa alınır. Görünür hedef yoksa hiçbir şey render etmez.
 */
const HomeTargets = () => {
  const dispatch = useDispatch();
  const userTargetList = useSelector((store) => store.home.userTargetList);
  const targetScope = useSelector((store) => store.home.targetScope);
  const salesRepList = useSelector((store) => store.home.salesRepList);
  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  const [openIds, setOpenIds] = useState([]);
  const [pastOpen, setPastOpen] = useState(false);
  const [openInfoId, setOpenInfoId] = useState(null);
  // Görüntülenen kapsam: "" = kendi hedeflerim, dolu ise sorumlu olunan bayinin kodu.
  const [selectedDealerCode, setSelectedDealerCode] = useState("");
  // Bölge müdürü kapsam tipi: bayi hedefleri mi, satış temsilcisi hedefleri mi.
  const [scopeType, setScopeType] = useState(SCOPE_DEALER);
  const [selectedSalesRepId, setSelectedSalesRepId] = useState("");

  // Seçici listeleri alfabetik: backend'in dönüş sırası (kayıt sırası) kullanıcı için anlamsız.
  const dealerOptions = useMemo(
    () => [...(targetScope?.dealers || [])].sort(byLabel(dealerLabel)),
    [targetScope]
  );
  // Kişisel kapsam (bölge müdüründe kapalı) ve temsilci kapsamı kararını backend verir;
  // ekran rol adı bilmez.
  const personalScopeAllowed = targetScope
    ? targetScope.personalScopeAllowed !== false
    : true;
  const salesRepScopeAllowed = Boolean(targetScope?.salesRepScopeAllowed);
  // Temsilci kapsamı yalnızca bölge müdüründe ve o kapsam seçiliyken aktiftir.
  const salesRepMode = salesRepScopeAllowed && scopeType === SCOPE_SALES_REP;

  const salesRepOptions = useMemo(() => {
    if (!salesRepScopeAllowed || !Array.isArray(salesRepList)) return [];
    return [...salesRepList].sort(byLabel((rep) => rep.name || ""));
  }, [salesRepScopeAllowed, salesRepList]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(homeAction.getTargetScope());
    }
  }, [dispatch, isAuthenticated]);

  // Kendi hedefi olmayan rolde (bölge müdürü) ilk bayi otomatik seçilir.
  useEffect(() => {
    if (!personalScopeAllowed && !selectedDealerCode && dealerOptions.length > 0) {
      setSelectedDealerCode(dealerOptions[0].code);
    }
  }, [personalScopeAllowed, dealerOptions, selectedDealerCode]);

  // Sorumlu olunan bayilere bağlı satış temsilcileri (bayi seçiminden bağımsız liste).
  useEffect(() => {
    if (salesRepScopeAllowed) {
      dispatch(homeAction.getMySalesReps());
    }
  }, [dispatch, salesRepScopeAllowed]);

  // Temsilci kapsamına geçilince listedeki ilk temsilci otomatik seçilir.
  useEffect(() => {
    if (salesRepMode && !selectedSalesRepId && salesRepOptions.length > 0) {
      setSelectedSalesRepId(String(salesRepOptions[0].employeeFirmId));
    }
  }, [salesRepMode, selectedSalesRepId, salesRepOptions]);

  // Kapsam değişince liste yeniden çekilir (kendi hedefim ↔ bayi ↔ temsilci).
  // Kapsam bilgisi gelmeden istek atılmaz; bölge müdüründe de seçim yapılana kadar beklenir,
  // aksi halde ona ait olmayan "kendi hedeflerim" isteği gider.
  useEffect(() => {
    if (!isAuthenticated || !targetScope) return;
    if (salesRepMode) {
      if (!selectedSalesRepId) return;
      dispatch(homeAction.getUserTargets("", selectedSalesRepId));
      return;
    }
    if (!personalScopeAllowed && !selectedDealerCode) return;
    dispatch(homeAction.getUserTargets(selectedDealerCode, ""));
  }, [
    dispatch,
    isAuthenticated,
    targetScope,
    personalScopeAllowed,
    salesRepMode,
    selectedDealerCode,
    selectedSalesRepId,
  ]);

  // Veri geldiğinde tüm aktif dönemleri açık başlat (birden fazla olabilir).
  useEffect(() => {
    if (Array.isArray(userTargetList) && userTargetList.length > 0) {
      setOpenIds(
        userTargetList
          .filter((p) => p.status === "ACTIVE")
          .map((p) => p.periodId)
      );
    }
  }, [userTargetList]);

  const togglePeriod = (periodId) => {
    setOpenIds((prev) =>
      prev.includes(periodId)
        ? prev.filter((id) => id !== periodId)
        : [...prev, periodId]
    );
  };

  // Temsilci kapsamında henüz seçim yapılmadıysa (ör. hiç temsilci yok) önceki kapsamın
  // listesi ekranda kalmasın.
  const periods =
    salesRepMode && !selectedSalesRepId
      ? []
      : (Array.isArray(userTargetList) ? userTargetList : []);
  // Seçici, sorumlu bayisi olan (CRG saha rolleri) üyelerde görünür.
  const hasDealerScope = dealerOptions.length > 0;

  // Hedefi olmayan üyede bölüm hiç görünmez; ancak bayi seçici varsa bölüm kalmalı,
  // aksi halde hedefi olmayan bir bayi seçildiğinde geri dönülemez.
  if (!hasDealerScope && periods.length === 0) {
    return null;
  }

  const selectedDealer = dealerOptions.find(
    (dealer) => dealer.code === selectedDealerCode
  );

  // Açıklama satırı yalnızca tek seçicili görünümde (CRG satış temsilcisi) gösterilir:
  // orada "Kendi hedeflerim" yerine bayi seçilince kişisel bloğun neden kalktığını anlatır.
  // Bölge müdüründe kapsam zaten seçicilerden okunuyor, ek açıklamaya gerek yok.
  const scopeNote =
    !salesRepScopeAllowed && selectedDealerCode
      ? `${
          selectedDealer?.name
            ? `${selectedDealer.name} bayisinin`
            : "Seçili bayinin"
        } dönem dönem toplam hedefi ve gerçekleşmesi gösteriliyor.`
      : "";

  const completedPeriods = periods.filter((p) => p.status === "COMPLETED");
  const currentPeriods = periods.filter((p) => p.status !== "COMPLETED");

  const renderPeriod = (period) => {
    const meta = STATUS_META[period.status] || STATUS_META.ACTIVE;
    const locked = period.status === "UPCOMING";
    const isOpen = !locked && openIds.includes(period.periodId);

    // Görünür (ölçümü olan) hedef grupları — bayi tipi ödül fallback'inin
    // yalnızca EN ALTTAKİ grupta gösterilebilmesi için önden hesaplanır.
    const renderableGroups = isOpen
      ? (period.targetGroups || [])
          .map((group, gi) => ({
            group,
            gi,
            measures: buildMeasures(group),
          }))
          .filter((item) => item.measures.length > 0)
      : [];
    const hasDealerTypeReward =
      Boolean(period.dealerTypeRewardInfo) ||
      (period.dealerTypeRewardAmount !== null &&
        period.dealerTypeRewardAmount !== undefined);

    return (
      <div
        className={`ht-period ${
          period.status === "ACTIVE" ? "active" : ""
        } ${locked ? "locked" : ""}`}
        key={period.periodId}
      >
        <button
          type="button"
          className="ht-period-head"
          disabled={locked}
          onClick={() => !locked && togglePeriod(period.periodId)}
        >
          <div className="ht-period-head-left">
            <span className="ht-period-name">{period.periodName}</span>
            <span className="ht-period-range">
              {formatDateRange(period.dateBegin, period.dateEnd)}
            </span>
            <span className={`ht-badge ${meta.className}`}>{meta.label}</span>
          </div>
          <div className="ht-period-head-right">
            {period.status === "ACTIVE" && period.dateEnd && (
              <span className="ht-period-end">
                Bitiş: {formatDate(period.dateEnd)}
              </span>
            )}
            {locked ? (
              <FaLock className="ht-lock" />
            ) : (
              <FaChevronDown className={`ht-chevron ${isOpen ? "open" : ""}`} />
            )}
          </div>
        </button>

        {isOpen && (
          <div className="ht-period-body">
            {renderableGroups.map(({ group, gi, measures }, ri) => {
              const hasMyTarget = Boolean(group.myBstTarget);
              // Kişisel ölçümler (Nokta Kayıt) bayi toplamı bölümünde listelenmez.
              const dealerMeasures = measures.filter((m) => !m.personalOnly);
              const perRow = Math.min(measures.length, 4);
              const colSpan = 12 / perRow;

              // Hedef grubunun kendi ödül tanımı varsa eski davranış:
              // bayi tipi ödülü hiç kullanılmaz. Kendi tanımı boşsa, bayi
              // tipi ödülü yalnızca en alttaki grupta gösterilir.
              const hasOwnReward =
                Boolean(group.rewardInfo) ||
                (group.rewardAmount !== null &&
                  group.rewardAmount !== undefined);
              const useDealerTypeReward =
                !hasOwnReward &&
                hasDealerTypeReward &&
                ri === renderableGroups.length - 1;
              let rewardInfo = null;
              let rewardAmount = null;
              if (hasOwnReward) {
                rewardInfo = group.rewardInfo;
                rewardAmount = group.rewardAmount;
              } else if (useDealerTypeReward) {
                rewardInfo = period.dealerTypeRewardInfo;
                rewardAmount = period.dealerTypeRewardAmount;
              }
              const hasReward = hasOwnReward || useDealerTypeReward;

              const infoKey = `${period.periodId}-${group.id ?? gi}`;
              const isInfoOpen = openInfoId === infoKey;

              return (
                <div className="ht-group" key={group.id ?? gi}>
                  <div className="ht-group-label">
                    {group.targetGroupName}
                    {group.description ? (
                      <button
                        type="button"
                        className={`ht-group-info-btn ${
                          isInfoOpen ? "open" : ""
                        }`}
                        aria-label="Açıklama için tıklayınız"
                        aria-expanded={isInfoOpen}
                        data-tooltip="Açıklama için tıklayınız"
                        onClick={() =>
                          setOpenInfoId(isInfoOpen ? null : infoKey)
                        }
                      >
                        <FaInfoCircle className="ht-group-info" />
                      </button>
                    ) : null}
                  </div>
                  {group.description && isInfoOpen ? (
                    <div className="ht-group-desc">
                      <FaInfoCircle style={{ marginTop: 2, flexShrink: 0 }} />
                      <span>{group.description}</span>
                    </div>
                  ) : null}

                  {/* Senin Hedefin — büyük donut'lar (ana odak) */}
                  {hasMyTarget && (
                    <div className="ht-personal">
                      <div className="ht-personal-head">
                        <span>👤 Senin Hedefin</span>
                        <span className="ht-pill">Sana Düşen</span>
                      </div>
                      <Row className="g-3">
                        {measures.map((m) => (
                          <Col xs={12} sm={6} lg={colSpan} key={m.key}>
                            <TargetDonut
                              title={m.title}
                              achieved={m.myAchieved}
                              target={m.myTarget}
                              unitLabel={m.unitLabel}
                            />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}

                  {/* Bayi Toplam Hedefi — kompakt çubuklar (alt referans) */}
                  {dealerMeasures.length > 0 && (
                    <div
                      className={`ht-dealer-compact ${
                        hasMyTarget ? "" : "standalone"
                      }`}
                    >
                      <div className="ht-dealer-compact-head">
                        🏪 Bayi Toplam Hedefi
                      </div>
                      <div className="ht-dealer-bars">
                        {dealerMeasures.map((m) => (
                          <DealerBar
                            key={m.key}
                            title={m.title}
                            achieved={m.bayiAchieved}
                            target={m.bayiTarget}
                            unitLabel={m.unitLabel}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hediye çeki bandı (Story 3) */}
                  {hasReward && (
                    <div className="ht-reward">
                      <span className="ht-reward-icon" aria-hidden="true">
                        🎁
                      </span>
                      <div className="ht-reward-body">
                        {/* Ödül hedef grubunun kendi tanımından geliyorsa bu hedefe
                            özeldir; bayi tipi fallback'i ise dönemin tamamı içindir. */}
                        <div className="ht-reward-title">
                          {hasOwnReward
                            ? "Bu hedefle kazanabileceğin hediye"
                            : "Bu dönemde kazanabileceğin hediye"}
                        </div>
                        <div className="ht-reward-sub">
                          {rewardInfo}
                        </div>
                      </div>
                      {rewardAmount !== null && rewardAmount !== undefined && (
                        <div className="ht-reward-amount">
                          {Number(rewardAmount).toLocaleString("tr-TR")} TL
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="home-targets">
      <Container>
        <div className="ht-section-head">
          <h2 className="ht-section-title">Hedef Durumu</h2>
        </div>

        {/* Kapsam seçici — hangi seçeneklerin çıkacağını backend'in kapsam bayrakları belirler:
              - CRG satış temsilcisi: tek seçici; "Kendi hedeflerim" + sorumlu olduğu bayiler,
              - CRG bölge müdürü: önce kapsam tipi ("Bayi" / "CRG Satış Temsilcisi"), ardından
                bayi veya temsilci seçimi; kendi hedefi yoktur.
            Bayi kapsamında kişisel pay gösterilmez, dönem dönem bayinin toplam hedefi ve
            gerçekleşmesi listelenir; temsilci kapsamında temsilcinin kendi hedef ekranı. */}
        {hasDealerScope && (
          <div className="ht-scope">
            {/* Bölge müdürü: önce kapsam tipi (bayi mi, temsilci mi). */}
            {salesRepScopeAllowed && (
              <div className="ht-scope-field">
                <label className="ht-scope-label" htmlFor="ht-scope-type">
                  Kapsam
                </label>
                <select
                  id="ht-scope-type"
                  className="ht-scope-select"
                  value={scopeType}
                  onChange={(e) => setScopeType(e.target.value)}
                >
                  <option value={SCOPE_DEALER}>Bayi</option>
                  <option value={SCOPE_SALES_REP}>CRG Satış Temsilcisi</option>
                </select>
              </div>
            )}

            {salesRepMode ? (
              <div className="ht-scope-field">
                <label className="ht-scope-label" htmlFor="ht-rep-select">
                  Satış Temsilcisi
                </label>
                <select
                  id="ht-rep-select"
                  className="ht-scope-select"
                  value={selectedSalesRepId}
                  onChange={(e) => setSelectedSalesRepId(e.target.value)}
                  disabled={salesRepOptions.length === 0}
                >
                  {salesRepOptions.length === 0 ? (
                    <option value="">Satış temsilcisi bulunamadı</option>
                  ) : (
                    salesRepOptions.map((rep) => (
                      <option
                        key={rep.employeeFirmId}
                        value={rep.employeeFirmId}
                      >
                        {rep.name || `#${rep.employeeFirmId}`}
                      </option>
                    ))
                  )}
                </select>
              </div>
            ) : (
              <div className="ht-scope-field">
                <label className="ht-scope-label" htmlFor="ht-scope-select">
                  {personalScopeAllowed ? "Görüntülenen hedefler" : "Bayi"}
                </label>
                <select
                  id="ht-scope-select"
                  className="ht-scope-select"
                  value={selectedDealerCode}
                  onChange={(e) => setSelectedDealerCode(e.target.value)}
                >
                  {personalScopeAllowed && (
                    <option value="">Kendi hedeflerim</option>
                  )}
                  {dealerOptions.map((dealer) => (
                    <option key={dealer.code} value={dealer.code}>
                      {dealerLabel(dealer)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {scopeNote && <span className="ht-scope-note">{scopeNote}</span>}
          </div>
        )}

        {periods.length === 0 && (
          <div className="ht-empty">
            {(() => {
              if (salesRepMode) {
                return salesRepOptions.length === 0
                  ? "Sorumlu olduğunuz bayilere bağlı satış temsilcisi bulunmuyor."
                  : "Bu satış temsilcisi için tanımlı hedef bulunmuyor.";
              }
              if (selectedDealerCode) {
                return "Bu bayi için tanımlı hedef bulunmuyor.";
              }
              return "Görüntülenecek hedef bulunmuyor.";
            })()}
          </div>
        )}

        {/* Geçmiş dönemler tek blokta toplanır; blok kapalı gelir. Aylık dönemlerle
            birlikte sayıları hızla arttığı için aktif dönemleri aşağı itmemeleri
            gerekir. */}
        {completedPeriods.length > 0 && (
          <div className={`ht-past ${pastOpen ? "open" : ""}`}>
            <button
              type="button"
              className="ht-past-head"
              aria-expanded={pastOpen}
              onClick={() => setPastOpen(!pastOpen)}
            >
              <span className="ht-past-title">
                Geçmiş dönemler ({completedPeriods.length})
              </span>
              <FaChevronDown
                className={`ht-chevron ${pastOpen ? "open" : ""}`}
              />
            </button>
            {pastOpen && (
              <div className="ht-past-body">
                {completedPeriods.map(renderPeriod)}
              </div>
            )}
          </div>
        )}

        {currentPeriods.map(renderPeriod)}
      </Container>
    </section>
  );
};

export default HomeTargets;
